<template>
	<div
		v-if="shouldShowChat"
		class="floating-chat"
		:class="{ 'floating-chat--open': isOpen }"
	>
		<transition name="chat-fade">
			<v-card
				v-if="isOpen"
				class="chat-container"
				elevation="10"
			>
				<header class="chat-header">
					<!-- Botão para abrir conversas no mobile - fixo no topo à esquerda -->
					<v-btn
						v-if="isMobile"
						icon
						small
						class="chat-mobile-conversations-toggle"
						@click="showMobileConversations = !showMobileConversations"
					>
						<v-icon>mdi-menu</v-icon>
					</v-btn>
					<div class="chat-header__title">
						<span class="chat-header__label">Assistente virtual</span>
						<h3 class="chat-header__headline">Fale com o Mévio</h3>
					</div>
					<div class="chat-header__actions">
						<v-btn
							icon
							small
							@click="loadConversations"
							:title="refreshTitle"
							:loading="loadingConversations"
						>
							<v-icon>mdi-refresh</v-icon>
						</v-btn>
						<v-btn
							icon
							small
							@click="handleClose"
							:title="closeButtonTitle"
						>
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>
				</header>

				<v-divider></v-divider>

				<section class="chat-body">
					<!-- Overlay para mobile quando conversas estão abertas -->
					<div
						v-if="isMobile && showMobileConversations"
						class="chat-mobile-overlay"
						@click="showMobileConversations = false"
					></div>

					<aside 
						class="chat-conversations"
						:class="{ 'chat-conversations--mobile-open': isMobile && showMobileConversations }"
						:style="!isMobile ? { width: conversationsWidth + 'px', minWidth: conversationsWidth + 'px', maxWidth: conversationsWidth + 'px' } : {}"
					>
						<div class="chat-conversations__header">
							<h4 class="chat-conversations__title">Conversas</h4>
							<div class="chat-conversations__header-actions">
								<v-btn
									text
									small
									class="chat-conversations__new"
									@click="createNewConversation"
								>
									<v-icon
										left
										small
										>mdi-plus</v-icon
									>
									<span class="chat-conversations__new-text">Nova conversa</span>
								</v-btn>
								<!-- Botão fechar no mobile -->
								<v-btn
									v-if="isMobile"
									icon
									small
									class="chat-conversations__close-mobile"
									@click="showMobileConversations = false"
								>
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</div>
						</div>

						<div class="chat-conversations__list">
							<template v-if="loadingConversations">
								<div
									v-for="index in 3"
									:key="`conv-loading-${index}`"
									class="conversation-item conversation-item--loading"
								>
									<v-skeleton-loader
										type="avatar, text"
										class="mb-2"
									/>
								</div>
							</template>

							<template v-else-if="conversationList.length">
								<v-list
									dense
									nav
									class="chat-conversations__items"
								>
									<v-list-item
										v-for="conversation in conversationList"
										:key="getConversationId(conversation)"
										:class="conversationClasses(conversation)"
										@click="selectConversation(conversation)"
									>
										<v-list-item-avatar
											class="conversation-avatar"
											size="44"
										>
											<span>{{ conversationAvatar(conversation) }}</span>
										</v-list-item-avatar>
										<v-list-item-content style="z-index: 99999999">
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-list-item-title v-bind="attrs" v-on="on">
														{{ conversationTitle(conversation) }}
													</v-list-item-title>
												</template>
												<span>{{ conversationTitle(conversation) }}</span>
											</v-tooltip>
											<v-list-item-subtitle class="conversation-subtitle-row">
												<span class="conversation-subtitle-text">
													{{ conversationSubtitle(conversation) }}
												</span>
												<span class="conversation-last-activity">
													{{ conversationLastActivity(conversation) }}
												</span>
											</v-list-item-subtitle>
										</v-list-item-content>
										<v-list-item-action class="conversation-item__action">
											<v-btn
												icon
												small
												@click.stop="deleteConversation(conversation)"
												color="error"
												class="conversation-delete-btn"
											>
												<v-icon small>mdi-delete</v-icon>
											</v-btn>
										</v-list-item-action>
										<v-badge
											v-if="conversationUnread(conversation)"
											color="#F15A24"
											dot
										></v-badge>
									</v-list-item>
								</v-list>
							</template>

							<div
								v-else
								class="chat-conversations__empty"
							>
								<v-icon color="#F15A24">mdi-chat-plus-outline</v-icon>
								<p>Inicie uma conversa para começar o atendimento.</p>
							</div>
						</div>
					</aside>

					<!-- Divisor redimensionável (apenas desktop) -->
					<div
						v-if="!isMobile"
						class="chat-resizer"
						@mousedown="startResize"
					></div>

					<main class="chat-thread">
						<header
							class="chat-thread__header"
							v-if="activeConversation"
						>
							<div>
								<h4>{{ conversationTitle(activeConversation) }}</h4>
								<small>{{ conversationSubtitle(activeConversation) }}</small>
							</div>
						</header>

						<div
							v-if="!activeConversation && !loadingConversations"
							class="chat-thread__placeholder"
						>
							<v-icon
								color="#F15A24"
								class="mb-3"
								>mdi-chat-processing-outline</v-icon
							>
							<p>Selecione uma conversa ou crie um novo atendimento.</p>
						</div>

						<div
							class="chat-thread__messages"
							ref="messagesContainer"
						>
							<template v-if="loadingMessages && !waitingAssistantResponse">
								<div
									v-for="index in 3"
									:key="`msg-loading-${index}`"
									class="message-bubble message-bubble--loading"
								>
									<v-skeleton-loader
										type="text"
										class="mb-2"
									/>
									<v-skeleton-loader
										type="text"
										width="60%"
									/>
								</div>
							</template>

							<template v-else-if="currentMessages.length">
								<div
									v-for="message in currentMessages"
									:key="message.id || message.timestamp"
									:class="messageClasses(message)"
								>
									<div class="message-bubble__content">
										<p
											class="message-bubble__text"
											v-html="formatMessageText(message)"
										></p>
										<div
											v-if="getMessageFiles(message).length > 0"
											class="message-bubble__attachments"
										>
											<div
												v-for="(arquivo, idx) in getMessageFiles(message)"
												:key="idx"
												class="message-attachment"
												@click="downloadFile(arquivo)"
											>
												<v-icon
													small
													class="message-attachment__icon"
													>{{ getFileIcon(arquivo.type || arquivo.filename_download) }}</v-icon
												>
												<span class="message-attachment__link">
													{{ arquivo.filename_download || 'Arquivo' }}
												</span>
												<v-icon
													x-small
													class="message-attachment__download"
													>mdi-download</v-icon
												>
											</div>
										</div>
										<!-- Botões quando a resposta cita lei/artigo ou sugere vade-mécum -->
										<div
											v-if="
												message.enviado_por === 'assistant' &&
												(message.id_lei ||
													shouldOfferVademecumFromMessage(message))
											"
											class="message-bubble__law-actions mt-2"
										>
											<v-btn
												v-if="message.id_lei"
												small
												outlined
												color="#f15a24"
												class="message-bubble__law-button"
												@click="goToLaw(message.id_lei, message.numero_lei, message.artigo_ref)"
											>
												<v-icon
													small
													left
													>mdi-book-open-variant</v-icon
												>
												Ir até a lei
											</v-btn>
											<v-btn
												v-if="message.id_lei"
												small
												outlined
												color="#f15a24"
												class="message-bubble__law-button"
												@click="openAnnotationEditorFromMessage(message)"
											>
												<v-icon
													small
													left
													>mdi-note-plus-outline</v-icon
												>
												Criar uma anotação
											</v-btn>
											<v-btn
												v-if="shouldOfferVademecumFromMessage(message)"
												small
												outlined
												color="#f15a24"
												class="message-bubble__law-button"
												@click="openVademecumEditorFromMessage(message)"
											>
												<v-icon
													small
													left
													>mdi-book-plus-outline</v-icon
												>
												Criar vade-mécum
											</v-btn>
										</div>
										<div class="message-bubble__footer">
											<span class="message-bubble__time">{{
												formatTimestamp(message)
											}}</span>
											<!-- Menu de opções (apenas respostas da IA) -->
											<v-menu
												v-if="message.enviado_por === 'assistant'"
												:close-on-content-click="true"
												offset-y
												left
												content-class="message-bubble-menu"
												transition="scale-transition"
											>
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														x-small
														class="message-bubble__menu-btn"
														v-bind="attrs"
														v-on="on"
														@click.stop
													>
														<v-icon small>mdi-dots-vertical</v-icon>
													</v-btn>
												</template>
												<v-list dense>
													<v-list-item
														@click="generateDocumentFromMessage(message)"
														:disabled="generatingDocumentId === (message.id || message.timestamp)"
													>
														<v-list-item-icon>
															<v-progress-circular
																v-if="generatingDocumentId === (message.id || message.timestamp)"
																indeterminate
																size="20"
																width="2"
															></v-progress-circular>
															<v-icon v-else>mdi-file-document-outline</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Gerar documento</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</div>
									</div>
								</div>
								<!-- Animação de "digitando..." enquanto aguarda resposta do assistant -->
								<div
									v-if="waitingAssistantResponse"
									class="message-bubble message-bubble--typing"
								>
									<div class="message-bubble__content">
										<div class="typing-indicator">
											<span></span>
											<span></span>
											<span></span>
										</div>
									</div>
								</div>
							</template>

							<div
								v-else-if="activeConversation && !waitingAssistantResponse"
								class="chat-thread__empty"
							>
								<v-icon
									color="#F15A24"
									class="mb-3"
									>mdi-forum-outline</v-icon
								>
								<p>
									Ainda não há mensagens aqui. Envie a primeira para começar.
								</p>
							</div>
							<!-- Animação de "digitando..." quando não há mensagens ainda -->
							<div
								v-if="
									activeConversation &&
									waitingAssistantResponse &&
									!currentMessages.length
								"
								class="message-bubble message-bubble--typing"
							>
								<div class="message-bubble__content">
									<div class="typing-indicator">
										<span></span>
										<span></span>
										<span></span>
									</div>
								</div>
							</div>
						</div>

						<footer class="chat-thread__footer">
							<div
								v-if="attachedFiles.length > 0"
								class="chat-thread__attachments-preview"
							>
								<div class="chat-thread__attachments-header">
									<v-icon
										small
										color="#F15A24"
										class="mr-2"
										>mdi-paperclip</v-icon
									>
									<span class="chat-thread__attachments-title">
										{{ attachedFiles.length }} arquivo{{ attachedFiles.length > 1 ? 's' : '' }} anexado{{ attachedFiles.length > 1 ? 's' : '' }}
									</span>
								</div>
								<div class="chat-thread__attachments-list">
									<div
										v-for="(file, index) in attachedFiles"
										:key="index"
										class="attachment-preview"
									>
										<v-icon
											small
											class="attachment-preview__icon"
											>{{ getFileIcon(file.name) }}</v-icon
										>
										<span class="attachment-preview__name">{{ file.name }}</span>
										<v-btn
											icon
											x-small
											class="attachment-preview__remove"
											@click="removeAttachment(index)"
										>
											<v-icon x-small>mdi-close</v-icon>
										</v-btn>
									</div>
								</div>
							</div>
							<div class="chat-thread__input-wrapper">
								<input
									ref="fileInput"
									type="file"
									multiple
									accept=".pdf,image/jpeg,image/png,image/gif,image/webp"
									style="display: none"
									@change="handleFileSelect"
								/>
								<v-textarea
									ref="messageInput"
									v-model="messageDraft"
									outlined
									dense
									hide-details
									color="#F15A24"
									label="Digite sua mensagem"
									:disabled="!activeConversation"
									class="chat-thread__input"
									rows="1"
									auto-grow
									@keydown.enter.exact="handleEnterKey"
									@focus="handleInputFocus"
								></v-textarea>
								<v-btn
									icon
									large
									color="#F15A24"
									class="chat-thread__attach"
									:class="{ 'chat-thread__attach--active': attachedFiles.length > 0 }"
									:disabled="!activeConversation || uploadingFiles"
									@click="$refs.fileInput.click()"
									:title="attachedFiles.length > 0 ? `${attachedFiles.length} arquivo(s) anexado(s)` : 'Anexar arquivo (max. 3)'"
								>
									<v-icon>mdi-paperclip</v-icon>
									<v-badge
										v-if="attachedFiles.length > 0"
										:content="attachedFiles.length"
										color="#F15A24"
										overlap
										class="chat-thread__attach-badge"
									>
									</v-badge>
								</v-btn>
								<v-btn
									icon
									large
									color="#F15A24"
									class="chat-thread__send"
									:disabled="isSendDisabled"
									@click="submitMessage"
								>
									<v-icon>mdi-send</v-icon>
								</v-btn>
							</div>
						</footer>
					</main>
				</section>
			</v-card>
		</transition>

		<v-fab-transition>
			<v-tooltip
				top
				:value="showMeioTooltip && !isOpen"
				:open-on-hover="false"
				content-class="floating-chat-meio-tooltip"
				transition="meio-tooltip-transition"
				:open-delay="0"
				:offset-y="10"
			>
				<template v-slot:activator="{ attrs }">
					<v-btn
						v-bind="attrs"
						@click="handleFloatingButtonClick"
						color="#F15A24"
						dark
						fab
						large
						class="floating-button"
						:title="buttonTitle"
					>
						<v-icon v-if="!isOpen">mdi-message-text-outline</v-icon>
						<v-icon v-else>mdi-close</v-icon>
					</v-btn>
				</template>
				<div class="meio-tooltip__content">
					<v-icon small class="meio-tooltip__icon">mdi-robot-outline</v-icon>
					<span class="meio-tooltip__text">Fale com o Mévio, seu agente de IA jurídico</span>
				</div>
			</v-tooltip>
		</v-fab-transition>

		<v-snackbar
			v-model="snackbarChat"
			:color="snackbarChatColor"
			:timeout="4000"
			top
		>
			{{ snackbarChatText }}
			<template v-slot:action="{ attrs }">
				<v-btn text v-bind="attrs" @click="snackbarChat = false">
					Fechar
				</v-btn>
			</template>
		</v-snackbar>

		<AiConsentDialog
			v-model="showAiConsentDialog"
			@accept="acceptConsent"
			@cancel="handleCancelAiConsent"
		/>

		<v-dialog
			v-model="showAnnotationEditorDialog"
			max-width="800px"
			scrollable
			@input="onAnnotationDialogToggle"
		>
			<v-card>
				<v-card-title class="pb-2">
					<span class="text-h6">Criar anotação na lei</span>
					<v-spacer></v-spacer>
					<v-btn icon aria-label="Fechar" @click="closeAnnotationEditorDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="annotation-editor-dialog__body">
					<p class="text-body-2 grey--text text--darken-1 mb-2">
						O texto da IA será salvo como anotação. Revise e edite antes de confirmar.
					</p>
					<p
						v-if="pendingAnnotationLabel"
						class="text-body-2 mb-3"
					>
						<strong>Destino:</strong> {{ pendingAnnotationLabel }}
					</p>
					<VueEditor
						ref="annotationVueEditor"
						use-custom-image-handler
						color="#F15A24"
						:editor-toolbar="annotationEditorToolbar"
						v-model="annotationEditorTexto"
						@image-added="handleAnnotationEditorImageAdded"
					/>
				</v-card-text>
				<v-card-actions class="px-4 pb-4">
					<v-btn text @click="closeAnnotationEditorDialog">Cancelar</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						color="#F15A24"
						dark
						:loading="loadingSaveAnnotationFromChat"
						@click="saveAnnotationFromEditor"
					>
						Salvar anotação
						<v-icon class="ml-1" small>mdi-content-save</v-icon>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog
			v-model="showVademecumEditorDialog"
			max-width="720px"
			scrollable
			@input="onVademecumDialogToggle"
		>
			<v-card>
				<v-card-title class="pb-2">
					<span class="text-h6">Criar vade-mécum</span>
					<v-spacer></v-spacer>
					<v-btn icon aria-label="Fechar" @click="closeVademecumEditorDialog">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="vademecum-editor-dialog__body">
					<p class="text-body-2 grey--text text--darken-1 mb-3">
						Revise o título e as leis sugeridas pela IA antes de confirmar.
					</p>
					<v-text-field
						v-model="vademecumEditorTitulo"
						outlined
						dense
						color="#F15A24"
						label="Título do vade-mécum"
						maxlength="30"
						counter="30"
						hide-details="auto"
						class="mb-4"
					/>
					<v-text-field
						v-model="vademecumEditorSearch"
						outlined
						dense
						color="#F15A24"
						placeholder="Buscar outra lei para incluir"
						append-icon="mdi-magnify"
						hide-details
						:loading="loadingVademecumSearch"
						class="mb-2"
						@keyup.enter="searchLeiToAddToVademecum"
						@click:append="searchLeiToAddToVademecum"
					/>
					<v-list
						v-if="vademecumEditorSearchResults.length"
						dense
						class="mb-3 vademecum-editor-dialog__search"
					>
						<v-list-item
							v-for="lei in vademecumEditorSearchResults"
							:key="'search-' + lei.id_lei"
							@click="addLeiToVademecumDraft(lei)"
						>
							<v-list-item-content>
								<v-list-item-title>{{ lei.numero_lei }}</v-list-item-title>
								<v-list-item-subtitle>{{ lei.nome_lei }}</v-list-item-subtitle>
							</v-list-item-content>
							<v-list-item-action>
								<v-icon small color="#F15A24">mdi-plus</v-icon>
							</v-list-item-action>
						</v-list-item>
					</v-list>
					<div
						v-if="loadingResolveVademecumLeis"
						class="d-flex align-center py-4"
					>
						<v-progress-circular
							indeterminate
							size="22"
							width="2"
							color="#F15A24"
							class="mr-3"
						></v-progress-circular>
						<span class="text-body-2">Localizando leis no catálogo…</span>
					</div>
					<template v-else>
						<p class="text-body-2 mb-2">
							<strong>{{ vademecumSelectedLeisCount }}</strong>
							lei{{ vademecumSelectedLeisCount === 1 ? '' : 's' }} selecionada{{
								vademecumSelectedLeisCount === 1 ? '' : 's'
							}}
						</p>
						<p
							v-if="!vademecumEditorLeis.length"
							class="text-body-2 grey--text"
						>
							Não encontrei normas do catálogo nesta resposta. Busque pelo
							número (ex.: 13.709) ou peça ao Mévio para listar as leis com
							número.
						</p>
						<v-checkbox
							v-for="lei in vademecumEditorLeis"
							:key="lei.id_lei"
							v-model="lei.selected"
							hide-details
							class="mt-0 pt-0 vademecum-editor-dialog__check"
							color="#F15A24"
						>
							<template v-slot:label>
								<span class="vademecum-editor-dialog__lei">
									<strong>{{ lei.numero_lei }}</strong>
									<span v-if="lei.nome_lei"> — {{ lei.nome_lei }}</span>
								</span>
							</template>
						</v-checkbox>
					</template>
				</v-card-text>
				<v-card-actions class="px-4 pb-4">
					<v-btn text @click="closeVademecumEditorDialog">Cancelar</v-btn>
					<v-spacer></v-spacer>
					<v-btn
						color="#F15A24"
						dark
						:loading="loadingSaveVademecumFromChat"
						:disabled="!canSaveVademecumFromChat"
						@click="saveVademecumFromEditor"
					>
						Salvar vade-mécum
						<v-icon class="ml-1" small>mdi-content-save</v-icon>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
	import { VueEditor } from 'vue2-editor';
	import AiConsentDialog from './AiConsentDialog.vue';
	import { useAiConsent } from '../composables/useAiConsent';
	import { renderMarkdown } from '../utils/markdown';

	export default {
		name: 'FloatingChat',
		components: {
			VueEditor,
			AiConsentDialog,
		},
		setup() {
			return useAiConsent();
		},
		data() {
			// Carregar largura salva do localStorage ou usar padrão
			const savedWidth = localStorage.getItem('chat-conversations-width');
			const defaultWidth = 320; // Aumentado de 280 para 320
			const conversationsWidth = savedWidth ? parseInt(savedWidth, 10) : defaultWidth;
			
			return {
				isOpen: false,
				messageDraft: '',
				selectedConversationId: null,
				conversations: [],
				messages: [],
				loadingConversations: false,
				loadingMessages: false,
				waitingAssistantResponse: false,
				showMobileConversations: false,
				conversationsWidth: conversationsWidth,
				isResizing: false,
				startX: 0,
				startWidth: conversationsWidth,
				showMeioTooltip: false,
				meioTooltipTimeout: null,
				generatingDocumentId: null,
				showAnnotationEditorDialog: false,
				annotationEditorTexto: '',
				pendingAnnotationMessage: null,
				loadingSaveAnnotationFromChat: false,
				annotationEditorToolbar: [
					[{ font: [] }, { size: [] }],
					['bold', 'italic', 'underline', 'strike'],
					[{ color: [] }, { background: [] }],
					[{ header: '1' }, { header: '2' }, 'blockquote', 'code-block'],
					[
						{ list: 'ordered' },
						{ list: 'bullet' },
						{ indent: '-1' },
						{ indent: '+1' },
					],
					['direction', { align: [] }],
					['link', 'image', 'video'],
					['clean'],
				],
				snackbarChat: false,
				snackbarChatText: '',
				snackbarChatColor: 'success',
				attachedFiles: [],
				uploadingFiles: false,
				showVademecumEditorDialog: false,
				vademecumEditorTitulo: '',
				vademecumEditorLeis: [],
				vademecumEditorSearch: '',
				vademecumEditorSearchResults: [],
				pendingVademecumMessage: null,
				loadingResolveVademecumLeis: false,
				loadingSaveVademecumFromChat: false,
				loadingVademecumSearch: false,
			};
		},
		computed: {
			shouldShowChat() {
				const isLoggedIn =
					this.$store.getters.isLoggedIn ||
					(this.$store.state.user && this.$store.state.user.id);
				const isInPainel = this.$route.path.startsWith('/painel');
				return isLoggedIn && isInPainel;
			},
			buttonTitle() {
				return this.isOpen ? 'Fechar Mévio' : 'Fale com o Mévio';
			},
			closeButtonTitle() {
				return 'Fechar chat';
			},
			refreshTitle() {
				return 'Atualizar conversas';
			},
			conversationList() {
				return this.conversations;
			},
			activeConversation() {
				if (!this.selectedConversationId) {
					return null;
				}
				return (
					this.conversationList.find(
						(conversation) =>
							this.getConversationId(conversation) ===
							this.selectedConversationId
					) || null
				);
			},
			currentMessages() {
				if (!this.selectedConversationId) {
					return [];
				}
				const filtered = this.messages.filter(
					(msg) => msg.conversa === this.selectedConversationId
				);
				return filtered.sort((a, b) => {
					const sortA = a.sort || 0;
					const sortB = b.sort || 0;
					return sortA - sortB;
				});
			},
			isSendDisabled() {
				return (
					!this.messageDraft.trim() ||
					!this.activeConversation ||
					this.loadingMessages ||
					this.uploadingFiles
				);
			},
			isMobile() {
				return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
			},
			pendingAnnotationLabel() {
				const message = this.pendingAnnotationMessage;
				if (!message) return '';
				const lei = message.numero_lei ? String(message.numero_lei) : 'Lei citada';
				const artigo =
					message.artigo_ref != null
						? message.artigo_ref
						: this.extractFirstArticleNumber(
								message.texto || message.text || message.content
							);
				if (artigo != null && !Number.isNaN(Number(artigo))) {
					return `Lei ${lei}, art. ${artigo}`;
				}
				return `Lei ${lei}`;
			},
			vademecumSelectedLeisCount() {
				return this.vademecumEditorLeis.filter((lei) => lei.selected).length;
			},
			canSaveVademecumFromChat() {
				return (
					Boolean(this.vademecumEditorTitulo && this.vademecumEditorTitulo.trim()) &&
					!this.loadingResolveVademecumLeis
				);
			},
			limiteVadeMecunsPorPlano() {
				const user = this.$store.state.user;
				const plano = user && user.plano ? user.plano : null;
				switch (plano) {
					case 'plano-free':
						return 10;
					case 'plano-carreira':
						return 10;
					case 'plano-premium':
						return 50;
					case 'plano-legislativo':
						return 100;
					default:
						return 3;
				}
			},
		},
		watch: {
			shouldShowChat(value) {
				if (value) {
					this.loadConversations();
					// Tooltip do Mévio: agendar quando o chat ficar visível (ex.: após login e ir para o painel)
					setTimeout(() => this.scheduleMeioTooltip(), 800);
				}
			},
			isOpen(value) {
				if (value) {
					this.dismissMeioTooltip();
					this.ensureSelectedConversation();
					const userId = this.$store.state.user && this.$store.state.user.id;
					this.promptConsentIfNeeded(userId);
				}
			},
			conversationList: {
				handler() {
					this.ensureSelectedConversation();
				},
				immediate: true,
			},
			currentMessages: {
				handler() {
					this.$nextTick(() => this.scrollThreadToBottom());
				},
				deep: true,
			},
			isMobile(value) {
				// Fechar drawer quando sair do mobile
				if (!value) {
					this.showMobileConversations = false;
				}
			},
		},
		methods: {
			/** Chave do localStorage por usuário: cada conta vê o tooltip uma vez e só de novo após 10 dias. */
			meioTooltipStorageKey() {
				const userId = this.$store.state.user && this.$store.state.user.id;
				return userId ? `floating-chat-tooltip-last-shown-${userId}` : 'floating-chat-tooltip-last-shown';
			},
			/** Verifica se o tooltip do Mévio deve ser exibido (nunca viu ou passaram 10 dias). */
			shouldShowMeioTooltip() {
				const KEY = this.meioTooltipStorageKey();
				const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;
				try {
					const raw = localStorage.getItem(KEY);
					return !raw ? true : (Date.now() - new Date(raw).getTime() >= TEN_DAYS_MS);
				} catch (error) {
					window.console.warn('Não foi possível verificar tooltip do Mévio:', error);
					return true;
				}
			},
			/** Esconde o tooltip e grava a data para só reaparecer em 10 dias (por usuário). */
			dismissMeioTooltip() {
				this.showMeioTooltip = false;
				if (this.meioTooltipTimeout) {
					clearTimeout(this.meioTooltipTimeout);
					this.meioTooltipTimeout = null;
				}
				try {
					localStorage.setItem(this.meioTooltipStorageKey(), new Date().toISOString());
				} catch (error) {
					window.console.warn('Não foi possível salvar data do tooltip do Mévio:', error);
				}
			},
			/** Abre o tooltip automaticamente uma vez e agenda fechamento em 5s. Não exibe se o chat já estiver aberto. */
			scheduleMeioTooltip() {
				const chatVisible = this.shouldShowChat;
				const shouldShow = this.shouldShowMeioTooltip();
				if (!chatVisible || !shouldShow || this.isOpen) return;
				this.showMeioTooltip = true;
				this.meioTooltipTimeout = setTimeout(() => {
					this.dismissMeioTooltip();
				}, 5000);
			},
			handleFloatingButtonClick() {
				this.dismissMeioTooltip();
				this.toggleChat();
			},
			handleCancelAiConsent() {
				const { shouldCloseChat } = this.cancelConsent();

				if (shouldCloseChat) {
					this.handleClose();
				}
			},
			toggleChat() {
				this.isOpen = !this.isOpen;
				this.emitState();
				if (this.isOpen) {
					this.ensureSelectedConversation();
				}
			},
			handleClose() {
				this.isOpen = false;
				this.emitState();
			},
			emitState() {
				this.$emit(this.isOpen ? 'open' : 'close');
			},
			ensureSelectedConversation() {
				if (!this.conversationList.length) {
					this.selectedConversationId = null;
					return;
				}
				if (!this.selectedConversationId) {
					this.selectedConversationId = this.getConversationId(
						this.conversationList[0]
					);
				}
			},
			async selectConversation(conversation) {
				const conversationId = this.getConversationId(conversation);
				if (!conversationId) {
					return;
				}
				this.selectedConversationId = conversationId;
				// Fechar drawer de conversas no mobile após selecionar
				if (this.isMobile) {
					this.showMobileConversations = false;
				}
				await this.loadMessages(conversationId);
			},
			submitMessage() {
				if (this.isSendDisabled) {
					return;
				}

				const messageText = this.messageDraft.trim();
				if (!messageText) {
					return;
				}

				const userId = this.$store.state.user && this.$store.state.user.id;
				this.withConsent(() => this.sendMessageToAssistant(), userId);
			},
			async sendMessageToAssistant() {
				const messageText = this.messageDraft.trim();
				if (!messageText) {
					return;
				}

				let uploadedFileIds = [];
				const filesToAttach = [...this.attachedFiles];
				if (filesToAttach.length > 0) {
					this.uploadingFiles = true;
					try {
						for (const file of filesToAttach) {
							const uploadedFile = await this.uploadFile(file);
							if (uploadedFile && uploadedFile.id) {
								uploadedFileIds.push(uploadedFile.id);
							}
						}
					} catch (error) {
						window.console.error('Erro ao fazer upload dos arquivos:', error);
						this.snackbarChatText = 'Erro ao anexar arquivos. Tente novamente.';
						this.snackbarChatColor = 'error';
						this.snackbarChat = true;
						this.uploadingFiles = false;
						return;
					}
					this.uploadingFiles = false;
				}

				this.messageDraft = '';
				this.attachedFiles = [];

				try {
					this.loadingMessages = true;
					this.waitingAssistantResponse = true;

					let conversationId = this.selectedConversationId;
					let isNewConversation = false;

					if (!conversationId) {
						const userId = this.$store.state.user && this.$store.state.user.id;
						if (!userId) {
							return;
						}

						const { data: newConversationData } = await this.$http.post(
							'/items/conversas',
							{
								titulo: 'Nova conversa',
								usuario: userId,
								status: 'published',
							}
						);

						if (newConversationData.data && newConversationData.data.id) {
							conversationId = newConversationData.data.id;
							this.selectedConversationId = conversationId;
							this.conversations.unshift(newConversationData.data);
							isNewConversation = true;
						} else {
							throw new Error('Erro ao criar conversa');
						}
					} else {
						const conversationMessages = this.messages.filter(
							(msg) => msg.conversa === conversationId
						);
						isNewConversation = conversationMessages.length === 0;
					}

					const assistantPayload = {
						prompt: messageText,
						conversation_id: conversationId,
					};

					if (isNewConversation) {
						assistantPayload.new = true;
					}

					if (uploadedFileIds.length > 0) {
						assistantPayload.file_ids = uploadedFileIds;
					}

					const { data: assistantResponse } = await this.$http.post(
						'/assistant',
						assistantPayload
					);

					if (assistantResponse.title) {
						await this.updateConversationTitleWithAnimation(
							conversationId,
							assistantResponse.title
						);
					}

					await this.loadMessages(conversationId);
					this.attachAssistantVademecumSuggestion(
						conversationId,
						assistantResponse
					);

					this.$nextTick(() => this.scrollThreadToBottom());
				} catch (error) {
					window.console.error('Erro ao enviar mensagem:', error);
				} finally {
					this.loadingMessages = false;
					this.waitingAssistantResponse = false;
				}
			},
			getConversationId(conversation) {
				if (!conversation) {
					return null;
				}
				return (
					conversation.id ||
					conversation.conversation_id ||
					conversation.uuid ||
					conversation.identifier ||
					null
				);
			},
			conversationTitle(conversation) {
				if (!conversation) {
					return 'Conversa';
				}
				return (
					conversation.titulo ||
					conversation.title ||
					conversation.name ||
					conversation.label ||
					'Nova conversa'
				);
			},
			conversationSubtitle(conversation) {
				if (!conversation) {
					return '';
				}

				const conversationId = this.getConversationId(conversation);
				const lastMessage = this.messages
					.filter((msg) => msg.conversa === conversationId)
					.sort(
						(a, b) => new Date(b.date_created) - new Date(a.date_created)
					)[0];

				if (lastMessage && lastMessage.texto) {
					const preview =
						lastMessage.texto.length > 50
							? lastMessage.texto.substring(0, 50) + '...'
							: lastMessage.texto;
					return preview;
				}

				return conversation.subtitle || conversation.description || '';
			},
			conversationLastActivity(conversation) {
				if (!conversation) {
					return '';
				}

				const conversationId = this.getConversationId(conversation);
				const lastMessage = this.messages
					.filter((msg) => msg.conversa === conversationId)
					.sort(
						(a, b) => new Date(b.date_created) - new Date(a.date_created)
					)[0];

				if (lastMessage && lastMessage.date_created) {
					return this.formatRelativeTime(lastMessage.date_created);
				}

				if (conversation.date_updated) {
					return this.formatRelativeTime(conversation.date_updated);
				}

				if (conversation.date_created) {
					return this.formatRelativeTime(conversation.date_created);
				}

				return '';
			},
			conversationUnread() {
				return 0;
			},
			conversationAvatar(conversation) {
				const title = this.conversationTitle(conversation);
				if (!title) {
					return 'L';
				}
				return title.slice(0, 1).toUpperCase();
			},
			conversationClasses(conversation) {
				return {
					'conversation-item': true,
					'conversation-item--active':
						this.getConversationId(conversation) ===
						this.selectedConversationId,
				};
			},
			messageClasses(message) {
				return {
					'message-bubble': true,
					'message-bubble--outgoing': this.isOutgoingMessage(message),
				};
			},
			isOutgoingMessage(message) {
				if (!message) {
					return false;
				}

				return message.enviado_por === 'user';
			},
			formatTimestamp(message) {
				if (!message) {
					return '';
				}

				if (message.timestamp) {
					return message.timestamp;
				}

				if (message.date_created) {
					return this.formatTime(message.date_created);
				}

				return '';
			},
			formatMessageText(message) {
				if (!message) {
					return '';
				}
				const text = message.texto || message.text || message.content || '';
				if (!text) {
					return '';
				}
				if (this.isOutgoingMessage(message)) {
					return this.convertUrlsToLinks(
						this.escapeMessageHtml(text).replace(/\n/g, '<br>')
					);
				}
				return renderMarkdown(text);
			},
			escapeMessageHtml(value) {
				return String(value)
					.replace(/&/g, '&amp;')
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;')
					.replace(/"/g, '&quot;');
			},
			convertUrlsToLinks(text) {
				const URLMatcher =
					/(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gim;
				return text.replace(
					URLMatcher,
					(found) =>
						`<a class="message-link" rel="noreferrer" target="_blank" href="${found}">${found}</a>`
				);
			},
			scrollThreadToBottom() {
				const container = this.$refs.messagesContainer;
				if (!container) {
					return;
				}
				container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
			},
			sanitizeTitle(title) {
				if (!title) return '';
				var slug = '';
				var titleLower = title.toLowerCase();
				slug = titleLower.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, 'e');
				slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, 'a');
				slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, 'o');
				slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, 'u');
				slug = slug.replace(/đ/gi, 'd');
				slug = slug.replace(/\s*$/g, '');
				slug = slug.replace(/\s+/g, '-');

				return slug;
			},
			/** Extrai o primeiro número de artigo citado no texto (ex.: "artigo 422", "Art. 422", "artigos 129 a 145" → 422 ou 129). */
			extractFirstArticleNumber(text) {
				if (!text || typeof text !== 'string') return null;
				const stripped = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
				// Art. 422, artigo 422, art. 422, artigos 129 a 145
				const match = stripped.match(/(?:artigos?|art\.?)\s*(\d+)/i);
				return match ? parseInt(match[1], 10) : null;
			},
			/**
			 * Extrai todos os números de artigo citados no texto (ex.: "artigos 186 e 927" → [186, 927]).
			 * Usado para só exibir "Criar uma anotação" quando houver exatamente um artigo citado.
			 */
			extractAllCitedArticleNumbers(text) {
				if (!text || typeof text !== 'string') return [];
				const stripped = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
				const numbers = [];
				// Padrões: "artigo 18", "art. 422", "artigos 186 e 927", "artigo 216-A", "artigos 129 a 145"
				const mainRegex = /(?:artigos?|art\.?)\s*(\d+)(?:\s*[-a-zA-Z])?/gi;
				const afterConjunction = /(?:e|,|\ba\s+)\s*(\d+)(?:\s*[-a-zA-Z])?/gi;
				let m;
				while ((m = mainRegex.exec(stripped)) !== null) {
					numbers.push(parseInt(m[1], 10));
				}
				while ((m = afterConjunction.exec(stripped)) !== null) {
					numbers.push(parseInt(m[1], 10));
				}
				// Remover duplicatas e ordenar (para consistência)
				return [...new Set(numbers)];
			},
			/** Verdadeiro quando a mensagem cita exatamente um artigo (uma anotação é por artigo). */
			hasSingleCitedArticle(message) {
				if (!message || message.enviado_por !== 'assistant') return false;
				const text = message.texto || message.text || message.content || '';
				const cited = this.extractAllCitedArticleNumbers(text);
				return cited.length === 1;
			},
			goToLaw(idLei, numeroLei, artigoRef) {
				if (!idLei) return;
				// Fechar o chat antes de navegar para a lei
				this.isOpen = false;
				this.emitState();
				this.$nextTick(() => {
					const slug = this.sanitizeTitle(numeroLei || '');
					const route = { path: `/painel/lei/${idLei}/${slug}` };
					if (artigoRef != null && !Number.isNaN(Number(artigoRef))) {
						route.query = { artigo: String(artigoRef) };
					}
					this.$router.push(route);
				});
			},
			/** Retorna o texto puro da mensagem (sem HTML). */
			getMessagePlainText(message) {
				if (!message) return '';
				const raw = message.texto || message.text || message.content || '';
				const div = document.createElement('div');
				div.innerHTML = raw;
				return (div.textContent || div.innerText || raw).trim();
			},
			/** Gera um documento de texto na galeria a partir da resposta da IA, nomeado com o título da conversa. */
			async generateDocumentFromMessage(message) {
				const messageKey = message.id || message.timestamp;
				const plainText = this.getMessagePlainText(message);
				if (!plainText) {
					this.snackbarChatText = 'Não há conteúdo para gerar o documento.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				if (!this.activeConversation) {
					this.snackbarChatText = 'Conversa não encontrada.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				const titulo = this.conversationTitle(this.activeConversation) || 'Documento do Mévio';
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					this.snackbarChatText = 'Faça login para salvar documentos.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				this.generatingDocumentId = messageKey;
				try {
					const docxMime =
						'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
					const blob = new Blob([plainText], { type: docxMime });
					const fileName = `${titulo.replace(/[<>:"/\\|?*]/g, '_').slice(0, 200)}.docx`;
					const file = new File([blob], fileName, { type: docxMime });
					const formData = new FormData();
					formData.append('image', file);
					const { data: uploadData } = await this.$http.post('/files', formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					});
					const asset = uploadData.data;
					if (!asset || !asset.id) {
						throw new Error('Resposta do upload inválida');
					}
					await this.$http.post('/items/documentos', {
						id_user: userId,
						documento: asset.id,
						titulo: titulo.slice(0, 255),
						status: 'published',
						tipo: asset.type || docxMime,
						pasta: undefined,
					});
					this.snackbarChatText = 'Documento criado na galeria de documentos.';
					this.snackbarChatColor = 'success';
					this.snackbarChat = true;
					// Opcional: navegar para a tela de documentos
					this.$router.push('/painel/documentos').catch(() => {});
				} catch (err) {
					window.console.error('Erro ao gerar documento:', err);
					const errMsg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0] && err.response.data.errors[0].message) || 'Erro ao gerar documento. Tente novamente.';
					this.snackbarChatText = errMsg;
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
				} finally {
					this.generatingDocumentId = null;
				}
			},
			/**
			 * Busca o id_reg (registro do artigo) na lei para poder vincular a anotação ao artigo.
			 * Retorna o id_reg do registro cujo conteudo corresponde ao número do artigo, ou null.
			 */
			async findIdRegByArticleNumber(idLei, artigoNum) {
				if (idLei == null || artigoNum == null) return null;
				const num = Number(artigoNum);
				if (Number.isNaN(num)) return null;
				try {
					const { data } = await this.$http.get(
						`items/conteudoleis/?limit=-1&sort=sort,id_reg&fields=id_reg,conteudo&filter[id_lei][_eq]=${idLei}`
					);
					const conteudos = data.data || [];
					const regex = new RegExp(
						'(?:^|[^\\d])(?:artigo|art\\.?)\\s*' + num + '(?:\\s|\\.|$|[^0-9])',
						'i'
					);
					const item = conteudos.find(function (c) {
						const text = (c.conteudo || '')
							.replace(/<[^>]+>/g, ' ')
							.replace(/\s+/g, ' ')
							.trim();
						return regex.test(text);
					});
					return item ? item.id_reg : null;
				} catch (err) {
					window.console.error('Erro ao buscar registro do artigo:', err);
					return null;
				}
			},
			/** Resolve o trecho da lei para a anotação: artigo citado ou primeiro registro. */
			async findIdRegForAnnotation(idLei, artigoNum) {
				if (idLei == null) return null;
				const fromArticle = await this.findIdRegByArticleNumber(idLei, artigoNum);
				if (fromArticle != null) return fromArticle;
				try {
					const { data } = await this.$http.get(
						`items/conteudoleis/?limit=1&sort=sort,id_reg&fields=id_reg&filter[id_lei][_eq]=${idLei}`
					);
					const first = data.data && data.data[0];
					return first ? first.id_reg : null;
				} catch (err) {
					window.console.error('Erro ao buscar trecho da lei:', err);
					return null;
				}
			},
			/** Converte texto puro em HTML inicial seguro para o VueEditor. */
			plainTextToInitialHtml(plain) {
				if (!plain || !plain.trim()) return '<p><br></p>';
				const esc = function (s) {
					return s
						.replace(/&/g, '&amp;')
						.replace(/</g, '&lt;')
						.replace(/>/g, '&gt;');
				};
				return plain
					.split(/\n\n+/)
					.map(function (block) {
						return '<p>' + esc(block).replace(/\n/g, '<br>') + '</p>';
					})
					.join('');
			},
			openAnnotationEditorFromMessage(message) {
				const plainText = this.getMessagePlainText(message);
				if (!plainText) {
					this.snackbarChatText = 'Não há conteúdo para criar a anotação.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				if (!message.id_lei) {
					this.snackbarChatText = 'Esta mensagem não referencia uma lei para vincular à anotação.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				this.pendingAnnotationMessage = message;
				const raw = message.texto || message.text || message.content || '';
				const markdownHtml = renderMarkdown(raw);
				this.annotationEditorTexto = markdownHtml
					? markdownHtml
					: this.plainTextToInitialHtml(plainText);
				this.showAnnotationEditorDialog = true;
			},
			closeAnnotationEditorDialog() {
				this.showAnnotationEditorDialog = false;
				this.pendingAnnotationMessage = null;
				this.annotationEditorTexto = '';
				this.loadingSaveAnnotationFromChat = false;
			},
			onAnnotationDialogToggle(open) {
				if (!open) {
					this.pendingAnnotationMessage = null;
					this.annotationEditorTexto = '';
					this.loadingSaveAnnotationFromChat = false;
				}
			},
			handleAnnotationEditorImageAdded(file, Editor, cursorLocation) {
				const formData = new FormData();
				formData.append('image', file);
				this.$http
					.post('/files', formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
					.then(function (response) {
						const url =
							'https://api.mylegis.com.br/assets/' + response.data.data.id;
						Editor.insertEmbed(cursorLocation, 'image', url);
					})
					.catch(function (err) {
						window.console.error('Erro ao enviar imagem na anotação:', err);
					});
			},
			/** Salva a anotação após edição no editor rico (mesma regra de lei/artigo/id_reg). */
			async saveAnnotationFromEditor() {
				const message = this.pendingAnnotationMessage;
				const htmlTexto = this.annotationEditorTexto || '';
				const textoLimpo = this.getMessagePlainText({
					texto: htmlTexto,
					text: htmlTexto,
					content: htmlTexto,
				});
				if (!textoLimpo) {
					this.snackbarChatText = 'Digite um conteúdo para a anotação.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				if (!message || !message.id_lei) {
					this.snackbarChatText = 'Conversa ou lei não encontrada.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					this.snackbarChatText = 'Faça login para criar anotações.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				this.loadingSaveAnnotationFromChat = true;
				try {
					const artigoNum =
						message.artigo_ref != null
							? message.artigo_ref
							: this.extractFirstArticleNumber(
									message.texto || message.text || message.content
								);
					const idReg = await this.findIdRegForAnnotation(
						message.id_lei,
						artigoNum
					);
					if (idReg == null) {
						this.snackbarChatText =
							'Não foi possível localizar a lei citada para salvar a anotação.';
						this.snackbarChatColor = 'warning';
						this.snackbarChat = true;
						return;
					}
					const payload = {
						texto: htmlTexto,
						id_lei: message.id_lei,
						id_reg: idReg,
						status: 'private',
					};
					await this.$http.post('/items/anotacoes', payload);
					this.closeAnnotationEditorDialog();
					this.snackbarChatText =
						'Anotação salva na lei citada. Use “Ir até a lei” para conferir.';
					this.snackbarChatColor = 'success';
					this.snackbarChat = true;
				} catch (err) {
					window.console.error('Erro ao criar anotação:', err);
					const errMsg =
						(err &&
							err.response &&
							err.response.data &&
							err.response.data.errors &&
							err.response.data.errors[0] &&
							err.response.data.errors[0].message) ||
						'Erro ao criar anotação. Tente novamente.';
					this.snackbarChatText = errMsg;
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
				} finally {
					this.loadingSaveAnnotationFromChat = false;
				}
			},
			isVademecumIntentText(text) {
				if (!text) return false;
				return /vade[\s-]?m[eé]cum|vademecum/i.test(String(text));
			},
			getPreviousUserMessage(assistantMessage) {
				const msgs = this.currentMessages || [];
				const idx = msgs.findIndex((m) => {
					if (assistantMessage.id && m.id) return m.id === assistantMessage.id;
					return m === assistantMessage;
				});
				if (idx < 0) return null;
				for (let i = idx - 1; i >= 0; i -= 1) {
					if (msgs[i].enviado_por !== 'assistant') return msgs[i];
				}
				return null;
			},
			shouldOfferVademecumFromMessage(message) {
				if (!message || message.enviado_por !== 'assistant') return false;
				if (message.suggestedVademecum) return true;
				if (
					this.isVademecumIntentText(
						message.texto || message.text || message.content
					)
				) {
					return true;
				}
				const previous = this.getPreviousUserMessage(message);
				return this.isVademecumIntentText(
					previous && (previous.texto || previous.text || previous.content)
				);
			},
			suggestVademecumTitle(userText) {
				if (!userText) return 'Vade-mécum do Mévio';
				const theme = userText.match(
					/legisla[cç][aã]o(?:\s+de)?\s+([^.\n]+?)(?:\s+e\s+as\s+novas|\s+em um|\s+para\s+meus|$)/i
				);
				if (theme && theme[1]) {
					let title = theme[1].trim();
					if (/TSE/i.test(userText) && !/TSE/i.test(title)) {
						title = `${title} e TSE`;
					}
					return title.slice(0, 30);
				}
				const named = userText.match(
					/vade[\s-]?m[eé]cum(?:\s+personalizado)?(?:\s+(?:de|sobre|chamado))?\s*[:\-]?\s*[“"']?([^"”'.\n]{3,40})/i
				);
				if (
					named &&
					named[1] &&
					!/^(para|com|em|e)\b/i.test(named[1].trim())
				) {
					return named[1].trim().slice(0, 30);
				}
				return 'Vade-mécum do Mévio';
			},
			extractLawSearchTerms(text) {
				const terms = [];
				const seen = {};
				const add = (raw) => {
					if (!raw) return;
					const t = String(raw).replace(/\s+/g, ' ').trim();
					if (t.length < 3) return;
					const key = t.toLowerCase();
					if (seen[key]) return;
					seen[key] = true;
					terms.push(t);
				};
				const plain = this.getMessagePlainText({ texto: text || '' });
				const pattern =
					/(?:lei|decreto(?:[\s-]?lei)?|medida provis[oó]ria|emenda constitucional|resolu[cç][aã]o(?:\s+(?:tse|cnj|stf|stj|tcu))?|instru[cç][aã]o normativa|portaria|s[uú]mula(?:\s+vinculante)?)\s*(?:n[ºo°.]{0,2}\s*)?(?:\d{1,3}(?:\.\d{3})+|\d{2,6})(?:\s*\/\s*\d{2,4})?/gi;
				let match;
				while ((match = pattern.exec(plain)) !== null) {
					add(match[0]);
				}
				if (/\bLGPD\b/i.test(plain)) add('13.709');
				if (/marco civil/i.test(plain)) add('12.965');
				if (/c[oó]digo de defesa do consumidor|\bCDC\b/i.test(plain)) {
					add('8.078');
				}
				return terms.slice(0, 12);
			},
			extractThematicSearchTerms(userText, assistantText) {
				const blob = `${userText || ''} ${assistantText || ''}`;
				const terms = [];
				if (/TSE|tribunal superior eleitoral/i.test(blob)) terms.push('TSE');
				if (/direito digital|prote[cç][aã]o de dados/i.test(blob)) {
					terms.push('proteção de dados');
				}
				if (/marco civil|internet/i.test(blob)) terms.push('marco civil');
				return terms;
			},
			pickLeiMatch(term, results) {
				if (!results || !results.length) return null;
				const digits = String(term).replace(/\D/g, '');
				if (digits.length >= 3) {
					const hit = results.find((lei) => {
						const numeroDigits = String(lei.numero_lei || '').replace(
							/\D/g,
							''
						);
						return numeroDigits.indexOf(digits) !== -1;
					});
					if (hit) return hit;
				}
				const termLow = String(term).toLowerCase();
				return (
					results.find((lei) => {
						const numero = String(lei.numero_lei || '').toLowerCase();
						const nome = String(lei.nome_lei || '').toLowerCase();
						return numero.indexOf(termLow) !== -1 || nome.indexOf(termLow) !== -1;
					}) || results[0]
				);
			},
			async searchLeisByTerm(term, limit) {
				const safe = String(term || '')
					.replace(/"/g, '\\"')
					.slice(0, 80);
				if (!safe) return [];
				const query = `
					query {
						leis(
							search: "${safe}",
							sort: ["-ranking_lei", "-data_lei"],
							limit: ${limit || 5},
							filter: { esfera_lei: { _nnull: true } }
						) {
							id_lei
							numero_lei
							nome_lei
							esfera_lei
						}
					}
				`;
				const response = await this.$http.post('/graphql', { query });
				return (
					(response.data &&
						response.data.data &&
						response.data.data.leis) ||
					[]
				);
			},
			mapLeiToDraft(lei, selected) {
				return {
					id_lei: lei.id_lei,
					numero_lei: lei.numero_lei || '',
					nome_lei: lei.nome_lei || '',
					selected: selected !== false,
				};
			},
			addLeiToVademecumDraft(lei, selected) {
				if (!lei || lei.id_lei == null) return;
				const exists = this.vademecumEditorLeis.some(
					(item) => String(item.id_lei) === String(lei.id_lei)
				);
				if (exists) {
					this.vademecumEditorLeis = this.vademecumEditorLeis.map((item) => {
						if (String(item.id_lei) === String(lei.id_lei)) {
							return { ...item, selected: true };
						}
						return item;
					});
				} else {
					this.vademecumEditorLeis = this.vademecumEditorLeis.concat([
						this.mapLeiToDraft(lei, selected !== false),
					]);
				}
				this.vademecumEditorSearchResults = [];
				this.vademecumEditorSearch = '';
			},
			async searchLeiToAddToVademecum() {
				const term = (this.vademecumEditorSearch || '').trim();
				if (!term) return;
				this.loadingVademecumSearch = true;
				try {
					this.vademecumEditorSearchResults = await this.searchLeisByTerm(
						term,
						8
					);
				} catch (err) {
					window.console.error('Erro ao buscar leis para o vade-mécum:', err);
					this.vademecumEditorSearchResults = [];
				} finally {
					this.loadingVademecumSearch = false;
				}
			},
			attachAssistantVademecumSuggestion(conversationId, assistantResponse) {
				if (!assistantResponse || !conversationId) return;
				const raw =
					assistantResponse.vademecum ||
					assistantResponse.suggested_vademecum ||
					null;
				if (!raw) return;
				const msgs = this.messages.filter(
					(m) =>
						m.conversa === conversationId && m.enviado_por === 'assistant'
				);
				const last = msgs.sort((a, b) => (a.sort || 0) - (b.sort || 0)).pop();
				if (!last) return;
				this.$set(last, 'suggestedVademecum', raw);
			},
			async resolveLeisFromSuggestedPayload(payload) {
				const list = (payload && (payload.leis || payload.laws)) || [];
				const resolved = [];
				for (let i = 0; i < list.length; i += 1) {
					const item = list[i];
					if (item && typeof item === 'object' && item.id_lei != null) {
						resolved.push(this.mapLeiToDraft(item, true));
						continue;
					}
					const id = typeof item === 'number' || typeof item === 'string' ? item : null;
					if (id == null) continue;
					try {
						const { data } = await this.$http.get(
							`/items/leis?fields=id_lei,numero_lei,nome_lei&filter[id_lei][_eq]=${id}&limit=1`
						);
						if (data.data && data.data[0]) {
							resolved.push(this.mapLeiToDraft(data.data[0], true));
						}
					} catch (err) {
						window.console.error('Erro ao resolver lei sugerida:', err);
					}
				}
				return resolved;
			},
			async openVademecumEditorFromMessage(message) {
				const previous = this.getPreviousUserMessage(message);
				const userText =
					(previous && (previous.texto || previous.text || previous.content)) ||
					'';
				const assistantText =
					message.texto || message.text || message.content || '';
				this.pendingVademecumMessage = message;
				this.vademecumEditorTitulo = (
					(message.suggestedVademecum &&
						(message.suggestedVademecum.titulo ||
							message.suggestedVademecum.title)) ||
					this.suggestVademecumTitle(userText)
				).slice(0, 30);
				this.vademecumEditorLeis = [];
				this.vademecumEditorSearch = '';
				this.vademecumEditorSearchResults = [];
				this.showVademecumEditorDialog = true;
				this.loadingResolveVademecumLeis = true;
				try {
					const collected = [];
					const seen = {};
					const pushLei = (lei, selected) => {
						if (!lei || lei.id_lei == null) return;
						const key = String(lei.id_lei);
						if (seen[key]) return;
						seen[key] = true;
						collected.push(this.mapLeiToDraft(lei, selected !== false));
					};

					if (message.suggestedVademecum) {
						const fromApi = await this.resolveLeisFromSuggestedPayload(
							message.suggestedVademecum
						);
						fromApi.forEach((lei) => pushLei(lei, true));
					}

					if (message.id_lei) {
						pushLei(
							{
								id_lei: message.id_lei,
								numero_lei: message.numero_lei,
								nome_lei: '',
							},
							true
						);
					}

					const numberedTerms = this.extractLawSearchTerms(
						`${userText}\n${assistantText}`
					);
					const numberedResults = await Promise.all(
						numberedTerms.map((term) =>
							this.searchLeisByTerm(term, 5)
								.then((results) => this.pickLeiMatch(term, results))
								.catch(() => null)
						)
					);
					numberedResults.forEach((lei) => pushLei(lei, true));

					if (collected.length < 8) {
						const thematic = this.extractThematicSearchTerms(
							userText,
							assistantText
						);
						const thematicResults = await Promise.all(
							thematic.map((term) =>
								this.searchLeisByTerm(term, 8).catch(() => [])
							)
						);
						thematicResults.forEach((list) => {
							(list || []).forEach((lei) => pushLei(lei, true));
						});
					}

					this.vademecumEditorLeis = collected.slice(0, 25);
				} catch (err) {
					window.console.error('Erro ao montar vade-mécum a partir do chat:', err);
					this.snackbarChatText =
						'Não foi possível localizar as leis sugeridas. Você pode buscá-las manualmente.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
				} finally {
					this.loadingResolveVademecumLeis = false;
				}
			},
			closeVademecumEditorDialog() {
				this.showVademecumEditorDialog = false;
				this.pendingVademecumMessage = null;
				this.vademecumEditorTitulo = '';
				this.vademecumEditorLeis = [];
				this.vademecumEditorSearch = '';
				this.vademecumEditorSearchResults = [];
				this.loadingResolveVademecumLeis = false;
				this.loadingSaveVademecumFromChat = false;
				this.loadingVademecumSearch = false;
			},
			onVademecumDialogToggle(open) {
				if (!open) {
					this.pendingVademecumMessage = null;
					this.vademecumEditorTitulo = '';
					this.vademecumEditorLeis = [];
					this.vademecumEditorSearch = '';
					this.vademecumEditorSearchResults = [];
					this.loadingResolveVademecumLeis = false;
					this.loadingSaveVademecumFromChat = false;
					this.loadingVademecumSearch = false;
				}
			},
			async countPersonalVademecums(userId) {
				const { data } = await this.$http.get(
					`/items/vademecums?fields=id&limit=-1&filter[user_created][_eq]=${userId}&filter[id_grupo][_null]=true`
				);
				return (data.data || []).length;
			},
			async saveVademecumFromEditor() {
				const titulo = (this.vademecumEditorTitulo || '').trim();
				if (!titulo) {
					this.snackbarChatText = 'Informe um título para o vade-mécum.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					this.snackbarChatText = 'Faça login para criar vade-mécuns.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				this.loadingSaveVademecumFromChat = true;
				try {
					const quantidade = await this.countPersonalVademecums(userId);
					if (quantidade >= this.limiteVadeMecunsPorPlano) {
						this.snackbarChatText = `Você atingiu o limite de ${this.limiteVadeMecunsPorPlano} vade-mécuns do seu plano.`;
						this.snackbarChatColor = 'warning';
						this.snackbarChat = true;
						return;
					}
					const { data } = await this.$http.post('/items/vademecums', {
						titulo: titulo.slice(0, 30),
						cor: '#F15A24',
						status: 'published',
					});
					const vadeId = data.data && data.data.id;
					if (!vadeId) {
						throw new Error('Resposta inválida ao criar vade-mécum');
					}
					const selected = this.vademecumEditorLeis.filter((lei) => lei.selected);
					for (let i = 0; i < selected.length; i += 1) {
						await this.$http.patch('/items/vademecums/' + vadeId, {
							leis: {
								create: [
									{
										leis_id_lei: { id_lei: selected[i].id_lei },
										vademecums_id: vadeId,
									},
								],
							},
						});
					}
					this.closeVademecumEditorDialog();
					this.snackbarChatText =
						selected.length > 0
							? `Vade-mécum “${titulo}” criado com ${selected.length} lei${selected.length === 1 ? '' : 's'}.`
							: `Vade-mécum “${titulo}” criado. Inclua leis depois em Legislações.`;
					this.snackbarChatColor = 'success';
					this.snackbarChat = true;
				} catch (err) {
					window.console.error('Erro ao criar vade-mécum pelo chat:', err);
					const errMsg =
						(err &&
							err.response &&
							err.response.data &&
							err.response.data.errors &&
							err.response.data.errors[0] &&
							err.response.data.errors[0].message) ||
						'Erro ao criar o vade-mécum. Tente novamente.';
					this.snackbarChatText = errMsg;
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
				} finally {
					this.loadingSaveVademecumFromChat = false;
				}
			},
			async updateConversationTitleWithAnimation(conversationId, newTitle) {
				const conversation = this.conversations.find(
					(conv) => this.getConversationId(conv) === conversationId
				);

				if (!conversation) {
					return;
				}

				const currentTitle = conversation.titulo || 'Nova conversa';
				const targetTitle = newTitle;
				let currentIndex = 0;

				if (currentTitle === targetTitle) {
					return;
				}

				const typeTitle = () => {
					if (currentIndex < targetTitle.length) {
						conversation.titulo = targetTitle.substring(0, currentIndex + 1);
						currentIndex++;
						setTimeout(typeTitle, 30);
					} else {
						conversation.titulo = targetTitle;
					}
				};

				typeTitle();
			},
			async loadConversations() {
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					return;
				}

				try {
					this.loadingConversations = true;

					const fields = 'id,titulo,usuario,date_created,date_updated,status';
					const { data } = await this.$http.get(
						`/items/conversas?fields=${fields}&filter[usuario][_eq]=${userId}&filter[status][_eq]=published&sort=-date_updated`
					);

					this.conversations = data.data || [];

					if (this.conversations.length > 0 && !this.selectedConversationId) {
						this.selectedConversationId = this.conversations[0].id;
						await this.loadMessages(this.conversations[0].id);
					}
				} catch (error) {
					window.console.error('Erro ao carregar conversas:', error);
					this.conversations = [];
				} finally {
					this.loadingConversations = false;
				}
			},
			async loadMessages(conversationId) {
				if (!conversationId) {
					this.messages = [];
					return;
				}

				try {
					this.loadingMessages = true;

					const fields =
						'id,texto,enviado_por,conversa,date_created,user_created,sort,lei_referenciada,lei_referenciada.id_lei,lei_referenciada.numero_lei,mensagens_files.directus_files_id.id,mensagens_files.directus_files_id.filename_download,mensagens_files.directus_files_id.type,arquivos.id,arquivos.filename_download,arquivos.type,arquivos.directus_files_id.id,arquivos.directus_files_id.filename_download,arquivos.directus_files_id.type';
					const { data } = await this.$http.get(
						`/items/mensagens?fields=${fields}&filter[conversa][_eq]=${conversationId}&sort=sort`
					);

					this.messages = this.messages.filter(
						(msg) => msg.conversa !== conversationId
					);

					const apiMessages = data.data || [];

					for (const message of apiMessages) {
						if (message.mensagens_files && Array.isArray(message.mensagens_files)) {
							message.mensagens_files = message.mensagens_files.filter(
								arq => arq && arq.directus_files_id
							);
						}
						if (message.arquivos && Array.isArray(message.arquivos)) {
							message.arquivos = message.arquivos.filter(
								arq => arq && (arq.directus_files_id || arq.id)
							);
						}

						if (
							message.enviado_por === 'assistant' &&
							message.lei_referenciada
						) {
							try {
								let lei = null;
								const ref = message.lei_referenciada;
								// Relação expandida: API retorna objeto { id_lei, numero_lei }
								if (ref && typeof ref === 'object' && (ref.id_lei != null || ref.id != null)) {
									lei = {
										id_lei: ref.id_lei != null ? ref.id_lei : ref.id,
										numero_lei: ref.numero_lei || '',
									};
								}
								// ID da lei (FK)
								if (!lei && typeof ref === 'number') {
									const { data: leisData } = await this.$http.get(
										`/items/leis?fields=id_lei,numero_lei&filter[id_lei][_eq]=${ref}&limit=1`
									);
									if (leisData.data && leisData.data.length > 0) lei = leisData.data[0];
								}
								// String (ex.: numero_lei "CPC")
								if (!lei && typeof ref === 'string') {
									const { data: leisData } = await this.$http.get(
										`/items/leis?fields=id_lei,numero_lei&filter[numero_lei][_eq]=${encodeURIComponent(
											ref
										)}&limit=1`
									);
									if (leisData.data && leisData.data.length > 0) lei = leisData.data[0];
								}
								if (lei) {
									this.$set(message, 'id_lei', lei.id_lei);
									this.$set(message, 'numero_lei', lei.numero_lei);
									const artigoRef = this.extractFirstArticleNumber(message.texto);
									if (artigoRef != null) this.$set(message, 'artigo_ref', artigoRef);
								}
							} catch (error) {
								window.console.error('Erro ao buscar lei referenciada:', error);
							}
						}
					}

					this.messages.push(...apiMessages);

					this.$nextTick(() => this.scrollThreadToBottom());
				} catch (error) {
					window.console.error('Erro ao carregar mensagens:', error);
				} finally {
					this.loadingMessages = false;
				}
			},
			async createNewConversation() {
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					return;
				}

				try {
					this.loadingConversations = true;

					const { data } = await this.$http.post('/items/conversas', {
						titulo: 'Nova conversa',
						usuario: userId,
						status: 'published',
					});

					if (data.data && data.data.id) {
						this.conversations.unshift(data.data);

						this.selectedConversationId = data.data.id;

						this.messages = this.messages.filter(
							(msg) => msg.conversa !== data.data.id
						);
					}
				} catch (error) {
					window.console.error('Erro ao criar conversa:', error);
				} finally {
					this.loadingConversations = false;
				}
			},
			async deleteConversation(conversation) {
				const conversationId = this.getConversationId(conversation);
				if (!conversationId) {
					return;
				}

				if (!confirm('Tem certeza que deseja deletar esta conversa?')) {
					return;
				}

				try {
					await this.$http.patch(`/items/conversas/${conversationId}`, {
						status: 'archived',
					});

					this.conversations = this.conversations.filter(
						(conv) => this.getConversationId(conv) !== conversationId
					);

					if (this.selectedConversationId === conversationId) {
						this.selectedConversationId = null;
						this.messages = this.messages.filter(
							(msg) => msg.conversa !== conversationId
						);

						if (this.conversations.length > 0) {
							this.selectedConversationId = this.conversations[0].id;
							await this.loadMessages(this.conversations[0].id);
						}
					}
				} catch (error) {
					window.console.error('Erro ao deletar conversa:', error);
					alert('Erro ao deletar conversa. Tente novamente.');
				}
			},
			formatTime(dateString) {
				if (!dateString) {
					return '';
				}

				const date = new Date(dateString);
				const now = new Date();
				const diffMs = now - date;
				const diffMins = Math.floor(diffMs / 60000);
				const diffHours = Math.floor(diffMs / 3600000);
				const diffDays = Math.floor(diffMs / 86400000);

				if (diffMins < 1) {
					return 'Agora';
				} else if (diffMins < 60) {
					return `${diffMins}min`;
				} else if (diffHours < 24) {
					return `${diffHours}h`;
				} else if (diffDays === 1) {
					return 'Ontem';
				} else if (diffDays < 7) {
					return `${diffDays}d`;
				} else {
					return date.toLocaleDateString('pt-BR', {
						day: '2-digit',
						month: '2-digit',
					});
				}
			},
			formatRelativeTime(dateString) {
				if (!dateString) {
					return '';
				}

				const date = new Date(dateString);
				const now = new Date();
				const diffMs = now - date;
				const diffMins = Math.floor(diffMs / 60000);
				const diffHours = Math.floor(diffMs / 3600000);
				const diffDays = Math.floor(diffMs / 86400000);

				if (diffMins < 1) {
					return 'Agora';
				} else if (diffMins < 60) {
					return `${diffMins}min`;
				} else if (diffHours < 24) {
					return `${diffHours}h`;
				} else if (diffDays === 1) {
					return 'Ontem';
				} else if (diffDays < 7) {
					return `${diffDays}d`;
				} else {
					return date.toLocaleDateString('pt-BR', {
						day: '2-digit',
						month: '2-digit',
					});
				}
			},
			handleViewportResize() {
				// Ajustar scroll quando o teclado aparecer/desaparecer
				this.$nextTick(() => {
					this.scrollThreadToBottom();
				});
			},
			handleInputFocus() {
				// Garantir que o input está acessível
				this.$nextTick(() => {
					if (this.$refs.messageInput && this.$refs.messageInput.$el) {
						const textarea = this.$refs.messageInput.$el.querySelector('textarea');
						if (textarea) {
							textarea.focus();
						}
					}
				});
			},
			handleEnterKey(event) {
				// No mobile, Enter sempre cria nova linha (comportamento padrão)
				if (this.isMobile) {
					return; // Permite o comportamento padrão (nova linha)
				}
				// No desktop, Enter envia a mensagem
				event.preventDefault();
				this.submitMessage();
			},
			startResize(event) {
				if (this.isMobile) return;
				this.isResizing = true;
				this.startX = event.clientX;
				this.startWidth = this.conversationsWidth;
				document.addEventListener('mousemove', this.handlePanelResize);
				document.addEventListener('mouseup', this.stopResize);
				event.preventDefault();
				event.stopPropagation();
			},
			handlePanelResize(event) {
				if (!this.isResizing || this.isMobile) return;
				
				const deltaX = event.clientX - this.startX;
				const newWidth = this.startWidth + deltaX;
				
				// Limites: mínimo 240px, máximo 500px
				const minWidth = 240;
				const maxWidth = 500;
				
				if (newWidth >= minWidth && newWidth <= maxWidth) {
					this.conversationsWidth = Math.round(newWidth);
					// Salvar no localStorage
					localStorage.setItem('chat-conversations-width', this.conversationsWidth.toString());
				}
			},
			stopResize() {
				this.isResizing = false;
				document.removeEventListener('mousemove', this.handlePanelResize);
				document.removeEventListener('mouseup', this.stopResize);
			},
			handleFileSelect(event) {
				const ALLOWED_TYPES = [
					'application/pdf',
					'image/jpeg',
					'image/png',
					'image/gif',
					'image/webp',
				];
				const MAX_FILES = 3;
				const MAX_SIZE_IMAGE = 20 * 1024 * 1024;
				const MAX_SIZE_PDF = 50 * 1024 * 1024;

				const files = Array.from(event.target.files || []);
				if (files.length === 0) return;

				const remaining = MAX_FILES - this.attachedFiles.length;
				if (remaining <= 0) {
					this.snackbarChatText = 'Limite de 3 arquivos atingido.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					if (this.$refs.fileInput) this.$refs.fileInput.value = '';
					return;
				}

				const toAdd = files.slice(0, remaining);
				if (files.length > remaining) {
					this.snackbarChatText = `Apenas ${remaining} arquivo(s) adicionado(s). Limite máximo: ${MAX_FILES}.`;
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
				}

				for (const file of toAdd) {
					if (!ALLOWED_TYPES.includes(file.type)) {
						this.snackbarChatText = `"${file.name}" não é suportado. Envie PDFs ou imagens (JPEG, PNG, GIF, WebP).`;
						this.snackbarChatColor = 'warning';
						this.snackbarChat = true;
						continue;
					}
					const maxSize = file.type === 'application/pdf' ? MAX_SIZE_PDF : MAX_SIZE_IMAGE;
					if (file.size > maxSize) {
						const limitMB = Math.round(maxSize / (1024 * 1024));
						this.snackbarChatText = `"${file.name}" excede o limite de ${limitMB}MB.`;
						this.snackbarChatColor = 'warning';
						this.snackbarChat = true;
						continue;
					}
					this.attachedFiles.push(file);
				}

				if (this.$refs.fileInput) {
					this.$refs.fileInput.value = '';
				}
			},
			removeAttachment(index) {
				this.attachedFiles.splice(index, 1);
				this.$nextTick(() => {
					if (this.$refs.messageInput && this.$refs.messageInput.$el) {
						const textarea = this.$refs.messageInput.$el.querySelector('textarea');
						if (textarea) {
							textarea.focus();
						}
					}
				});
			},
			async uploadFile(file) {
				const formData = new FormData();
				formData.append('file', file);

				const { data } = await this.$http.post('/files', formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
				return data.data;
			},
			getMessageFiles(message) {
				if (!message) {
					return [];
				}
				const seen = new Set();
				const out = [];
				const pushFile = (file) => {
					if (!file || !file.id) {
						return;
					}
					if (seen.has(file.id)) {
						return;
					}
					seen.add(file.id);
					out.push(file);
				};
				const rowToFile = (row) => {
					if (!row) {
						return;
					}
					if (typeof row === 'string') {
						pushFile({ id: row });
						return;
					}
					if (row.directus_files_id != null && row.directus_files_id !== '') {
						const d = row.directus_files_id;
						if (typeof d === 'string') {
							pushFile({ id: d });
						} else if (d && d.id) {
							pushFile(d);
						}
						return;
					}
					if (row.filename_download != null || row.type != null) {
						if (row.id) {
							pushFile(row);
						}
					}
				};
				if (message.mensagens_files && Array.isArray(message.mensagens_files)) {
					message.mensagens_files.forEach(rowToFile);
				}
				const arq = message.arquivos;
				if (arq) {
					if (Array.isArray(arq)) {
						arq.forEach(rowToFile);
					} else if (typeof arq === 'string') {
						pushFile({ id: arq });
					} else {
						rowToFile(arq);
					}
				}
				return out;
			},
			getFileIcon(nameOrType) {
				if (!nameOrType) return 'mdi-file';

				const mimeMap = {
					'application/pdf': 'mdi-file-pdf-box',
					'image/jpeg': 'mdi-file-image',
					'image/png': 'mdi-file-image',
					'image/gif': 'mdi-file-image',
					'image/webp': 'mdi-file-image',
				};
				if (mimeMap[nameOrType]) return mimeMap[nameOrType];

				const ext = String(nameOrType).split('.').pop().toLowerCase();
				const extMap = {
					pdf: 'mdi-file-pdf-box',
					jpg: 'mdi-file-image',
					jpeg: 'mdi-file-image',
					png: 'mdi-file-image',
					gif: 'mdi-file-image',
					webp: 'mdi-file-image',
					doc: 'mdi-file-word-box',
					docx: 'mdi-file-word-box',
				};
				return extMap[ext] || 'mdi-file';
			},
			downloadFile(arquivo) {
				if (!arquivo || !arquivo.id) return;
				const url = `${this.$http.defaults.baseURL}/assets/${arquivo.id}?download`;
				window.open(url, '_blank');
			},
		},
		async mounted() {
			if (this.shouldShowChat) {
				await this.loadConversations();
				// Tooltip automático do Mévio: exibe uma vez e só reaparece após 10 dias
				setTimeout(() => this.scheduleMeioTooltip(), 800);
			}
			// Adicionar listener para ajustar scroll quando teclado aparecer (mobile)
			if (this.isMobile) {
				window.addEventListener('resize', this.handleViewportResize);
			}
		},
		beforeDestroy() {
			this.dismissMeioTooltip();
			if (this.isMobile) {
				window.removeEventListener('resize', this.handleViewportResize);
			}
			// Limpar listeners de redimensionamento
			this.stopResize();
		},
	};
</script>

<style scoped>
	.floating-chat {
		position: fixed;
		right: 24px;
		bottom: 24px;
		z-index: 2100;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 16px;
		max-width: calc(100vw - 48px);
	}

	.chat-container {
		width: 1000px;
		max-width: calc(100vw - 48px) !important;
		max-height: calc(90vh - 48px) !important;
		display: flex;
		flex-direction: column;
		border-radius: 16px;
		overflow: hidden;
		box-sizing: border-box;
	}

	.chat-container ::v-deep .v-card__text {
		overflow: visible;
	}

	.chat-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 5px 20px;
		position: relative;
	}

	.chat-mobile-conversations-toggle {
		position: absolute;
		left: 16px;
		top: 50%;
		transform: translateY(-50%);
		z-index: 10;
		flex-shrink: 0;
	}

	.chat-header__actions {
		display: flex;
		gap: 8px;
	}

	.chat-header__label {
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #8f8f8f;
	}

	.chat-header__title {
		flex: 1;
		margin-left: 0;
		min-width: 0;
		overflow: hidden;
	}

	.chat-header__title:has(~ .chat-header__actions) {
		margin-right: 8px;
	}

	.chat-header__headline {
		margin: 4px 0 0;
		font-size: 1.1rem;
		color: #1f1f1f;
	}

	.chat-header__title:has(~ .chat-header__actions) {
		margin-right: 8px;
	}

	.chat-body {
		flex: 1;
		padding: 16px;
		background: linear-gradient(
			135deg,
			rgba(241, 90, 36, 0.05),
			rgba(241, 90, 36, 0.03)
		);
		display: flex;
		gap: 0;
		align-items: stretch;
		min-height: 0;
		box-sizing: border-box;
		max-height:73vh;
		position: relative;
		overflow: hidden;
	}
	
	.chat-resizer {
		width: 4px;
		background-color: transparent;
		cursor: col-resize;
		position: relative;
		flex-shrink: 0;
		z-index: 10;
		margin: 0 8px;
		transition: background-color 0.2s ease;
	}
	
	.chat-resizer:hover {
		background-color: rgba(241, 90, 36, 0.3);
	}
	
	.chat-resizer::before {
		content: '';
		position: absolute;
		left: -2px;
		right: -2px;
		top: 0;
		bottom: 0;
		cursor: col-resize;
	}
	
	.chat-body.resizing {
		user-select: none;
		cursor: col-resize;
	}
	
	.chat-body.resizing .chat-resizer {
		background-color: rgba(241, 90, 36, 0.5);
	}

	.floating-button {
		box-shadow: 0 12px 24px rgba(241, 90, 36, 0.3);
	}

	.chat-fade-enter-active,
	.chat-fade-leave-active {
		transition: all 0.2s ease-out;
	}

	.chat-fade-enter,
	.chat-fade-leave-to {
		opacity: 0;
		transform: translateY(12px);
	}

	.chat-conversations {
		flex: 0 0 auto;
		display: flex;
		flex-direction: column;
		background-color: #ffffff;
		border-radius: 12px;
		padding: 16px;
		padding-top: 20px;
		box-shadow: inset 0 0 0 1px rgba(241, 90, 36, 0.1);
		min-height: 0;
		box-sizing: border-box;
		position: relative;
		z-index: 1;
		overflow: hidden;
		transition: width 0.1s ease-out;
	}

	.chat-conversations__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-top: 4px;
		gap: 12px;
		flex-wrap: nowrap;
		position: relative;
		min-height: 40px;
	}

	.chat-conversations__title {
		font-size: 1rem;
		font-weight: 600;
		margin: 0;
		padding-top: 2px;
		line-height: 1.4;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		flex: 1;
		min-width: 0;
	}

	.chat-conversations__header-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	.chat-conversations__header h4 {
		font-size: 1rem;
		font-weight: 600;
		margin: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.chat-conversations__new {
		text-transform: none;
		font-weight: 500;
		color: #f15a24;
		padding: 0 8px;
		min-width: auto;
		white-space: nowrap;
		flex-shrink: 0;
		margin: 0;
	}

	.chat-conversations__close-mobile {
		flex-shrink: 0;
		margin: 0;
		position: relative;
	}

	.chat-conversations__new-text {
		display: inline;
	}

	.chat-conversations__list {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 4px;
		min-height: 0;
		-webkit-overflow-scrolling: touch;
		position: relative;
		z-index: 1;
	}

	.chat-conversations__list::-webkit-scrollbar {
		width: 6px;
	}

	.chat-conversations__list::-webkit-scrollbar-thumb {
		border-radius: 6px;
		background: rgba(241, 90, 36, 0.3);
	}

	.chat-conversations__items {
		background: transparent;
		min-height: 100%;
		position: relative;
		z-index: 1;
	}

	.conversation-item {
		border-radius: 12px;
		margin-bottom: 6px;
		transition: background-color 0.2s ease;
		padding: 8px 4px !important;
		cursor: pointer;
		position: relative;
		z-index: 1;
	}

	.conversation-item:active {
		background-color: rgba(241, 90, 36, 0.2);
		transition: background-color 0.1s ease;
	}

	.conversation-item .v-list-item__action,
	.conversation-item__action {
		position: relative;
		z-index: 10;
		flex-shrink: 0;
	}

	.conversation-delete-btn {
		position: relative;
		z-index: 11;
	}

	.conversation-item--active {
		background: rgba(241, 90, 36, 0.12);
	}

	.conversation-avatar {
		background: rgba(241, 90, 36, 0.12);
		color: #f15a24;
		font-weight: 600;
		font-size: 1.1rem !important;
	}

	.conversation-avatar span {
		font-size: 1.1rem;
		font-weight: 600;
	}

	.conversation-item .v-list-item__title {
		font-size: 0.95rem !important;
		font-weight: 500 !important;
		line-height: 1.4 !important;
		margin-bottom: 2px;
	}
	
	/* Ajustar z-index do tooltip para aparecer acima do chat */
	.conversation-item ::v-deep .v-tooltip__content {
		z-index: 99999999 !important;
	}

	.conversation-item .v-list-item__subtitle {
		font-size: 0.85rem !important;
		line-height: 1.3 !important;
		opacity: 0.8;
	}

	.conversation-subtitle-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 8px;
		margin-top: 2px;
	}

	.conversation-subtitle-text {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0;
	}

	.conversation-last-activity {
		font-size: 0.75rem !important;
		color: #8f8f8f;
		flex-shrink: 0;
		white-space: nowrap;
	}

	.conversation-delete-btn {
		margin-left: 8px;
		position: relative;
		z-index: 11;
	}

	.chat-conversations__empty {
		margin-top: 24px;
		text-align: center;
		color: #6f6f6f;
		font-size: 0.9rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.chat-thread {
		flex: 1;
		min-width: 0;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background-color: rgba(255, 255, 255, 0.92);
		border-radius: 12px;
		padding: 12px 16px 16px;
		box-shadow: inset 0 0 0 1px rgba(241, 90, 36, 0.1);
		box-sizing: border-box;
		overflow: hidden;
		position: relative;
		z-index: 0;
	}

	.chat-thread__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 12px;
	}

	.chat-thread__header h4 {
		margin: 0;
		padding-top: 2px;
		font-size: 1rem;
		font-weight: 600;
		line-height: 1.4;
	}

	.chat-thread__header small {
		color: #6f6f6f;
	}

	.chat-thread__messages {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 6px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		min-height: 180px;
		min-width: 0;
		-webkit-overflow-scrolling: touch;
		position: relative;
		z-index: 0;
	}

	.chat-thread__messages::-webkit-scrollbar {
		width: 6px;
	}

	.chat-thread__messages::-webkit-scrollbar-thumb {
		border-radius: 6px;
		background: rgba(241, 90, 36, 0.3);
	}

	.chat-thread__placeholder,
	.chat-thread__empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		justify-content: center;
		margin-top: 40px;
		color: #6f6f6f;
		gap: 12px;
	}

	.message-bubble {
		align-self: flex-start;
		background: rgba(241, 90, 36, 0.09);
		border-radius: 16px 16px 16px 4px;
		padding: 12px 16px;
		max-width: 82%;
		line-height: 1.4;
		word-wrap: break-word;
		overflow-wrap: break-word;
		position: relative;
		z-index: 0;
	}

	.message-bubble--outgoing {
		align-self: flex-end;
		background: #f15a24;
		color: white;
		border-radius: 16px 16px 4px 16px;
	}

	.message-bubble--outgoing .message-bubble__time {
		color: rgba(255, 255, 255, 0.9);
	}

	.message-bubble__content {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.message-bubble__text {
		margin: 0;
		font-size: 0.95rem;
		word-break: break-word;
		line-height: 1.55;
	}

	.message-bubble__text ::v-deep p {
		margin: 0 0 0.65em;
	}

	.message-bubble__text ::v-deep p:last-child {
		margin-bottom: 0;
	}

	.message-bubble__text ::v-deep h1,
	.message-bubble__text ::v-deep h2,
	.message-bubble__text ::v-deep h3,
	.message-bubble__text ::v-deep h4 {
		margin: 0.85em 0 0.4em;
		font-size: 1em;
		font-weight: 700;
		line-height: 1.35;
	}

	.message-bubble__text ::v-deep h1:first-child,
	.message-bubble__text ::v-deep h2:first-child,
	.message-bubble__text ::v-deep h3:first-child {
		margin-top: 0;
	}

	.message-bubble__text ::v-deep ul,
	.message-bubble__text ::v-deep ol {
		margin: 0.4em 0 0.75em;
		padding-left: 1.25em;
	}

	.message-bubble__text ::v-deep li {
		margin: 0.2em 0;
	}

	.message-bubble__text ::v-deep strong {
		font-weight: 700;
	}

	.message-bubble__text ::v-deep code {
		font-size: 0.88em;
		padding: 0.1em 0.3em;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.08);
	}

	.message-bubble__text ::v-deep pre {
		margin: 0.6em 0;
		padding: 0.7em 0.8em;
		overflow-x: auto;
		border-radius: 8px;
		background: rgba(0, 0, 0, 0.08);
	}

	.message-bubble__text ::v-deep pre code {
		padding: 0;
		background: transparent;
	}

	.message-bubble__text ::v-deep blockquote {
		margin: 0.6em 0;
		padding-left: 0.75em;
		border-left: 3px solid rgba(241, 90, 36, 0.45);
		opacity: 0.92;
	}

	.message-bubble__footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 4px;
	}

	.message-bubble__time {
		font-size: 0.75rem;
		opacity: 0.7;
	}

	.message-bubble__menu-btn {
		margin: -4px -4px -4px 0;
		opacity: 0.75;
	}

	.message-bubble__menu-btn:hover {
		opacity: 1;
	}

	.message-bubble__law-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		align-self: flex-start;
	}

	.message-bubble__law-button {
		align-self: flex-start;
		text-transform: none;
	}

	/* Links gerados via v-html não recebem o atributo do scoped,
		então precisamos usar ::v-deep para o CSS alcançar o <a>. */
	.chat-thread__messages ::v-deep .message-link,
	.chat-thread__messages ::v-deep .message-link:visited {
		color: inherit !important;
		text-decoration: underline;
	}

	/* No balão de saída (laranja), forçar links em branco (não azul). */
	.message-bubble--outgoing ::v-deep .message-link,
	.message-bubble--outgoing ::v-deep .message-link:visited {
		color: #fff !important;
	}

	.message-bubble--typing {
		align-self: flex-start;
		background: rgba(241, 90, 36, 0.09);
		border-radius: 16px 16px 16px 4px;
		padding: 16px 20px;
		max-width: 82px;
	}

	.typing-indicator {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 0;
	}

	.typing-indicator span {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background-color: rgba(241, 90, 36, 0.6);
		display: inline-block;
		animation: typing-bounce 1.4s infinite ease-in-out;
	}

	.typing-indicator span:nth-child(1) {
		animation-delay: -0.32s;
	}

	.typing-indicator span:nth-child(2) {
		animation-delay: -0.16s;
	}

	@keyframes typing-bounce {
		0%,
		80%,
		100% {
			transform: scale(0.8);
			opacity: 0.5;
		}
		40% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.chat-conversations__list .v-skeleton-loader {
		margin-bottom: 8px;
		border-radius: 12px;
	}

	.chat-thread__messages .v-skeleton-loader {
		margin-bottom: 12px;
		border-radius: 16px;
	}

	.message-bubble--loading {
		align-self: flex-start;
		background: rgba(241, 90, 36, 0.05);
		border-radius: 16px 16px 16px 4px;
		padding: 12px 16px;
		max-width: 70%;
		animation: pulse-loading 1.5s ease-in-out infinite;
	}

	.conversation-item--loading {
		padding: 8px 4px;
		animation: pulse-loading 1.5s ease-in-out infinite;
	}

	@keyframes pulse-loading {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.6;
		}
	}

	.chat-thread__footer {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 16px;
		flex-shrink: 0;
		width: 100%;
		position: relative;
		z-index: 10;
		background: transparent;
	}

	.chat-thread__input {
		flex: 1;
		min-width: 0;
		position: relative;
		z-index: 1;
		transition: all 0.2s ease;
	}

	.chat-thread__input--with-attachments ::v-deep .v-input__control {
		border-color: rgba(241, 90, 36, 0.4) !important;
	}

	.chat-thread__input--with-attachments ::v-deep .v-text-field--outlined fieldset {
		border-color: rgba(241, 90, 36, 0.4) !important;
		border-width: 2px !important;
	}

	.chat-thread__input ::v-deep .v-text-field__slot {
		align-items: flex-end;
	}

	.chat-thread__input ::v-deep textarea {
		resize: none;
		overflow-y: auto;
		max-height: 120px;
		pointer-events: auto;
	}

	.chat-thread__input ::v-deep .v-input__control {
		pointer-events: auto;
	}

	.chat-thread__send {
		box-shadow: none;
	}

	.chat-thread__input-wrapper {
		display: flex;
		align-items: flex-end;
		gap: 8px;
		width: 100%;
		flex-shrink: 0;
		position: relative;
		z-index: 10;
	}

	.chat-thread__attach {
		flex-shrink: 0;
		box-shadow: none;
		position: relative;
		transition: all 0.2s ease;
	}

	.chat-thread__attach--active {
		background-color: rgba(241, 90, 36, 0.1) !important;
	}

	.chat-thread__attach-badge {
		position: absolute;
		top: 4px;
		right: 4px;
	}

	.chat-thread__attachments-preview {
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
		padding: 12px;
		background: rgba(241, 90, 36, 0.08);
		border: 2px solid rgba(241, 90, 36, 0.2);
		border-radius: 8px;
		flex-shrink: 0;
		box-shadow: 0 2px 4px rgba(241, 90, 36, 0.1);
	}

	.chat-thread__attachments-header {
		display: flex;
		align-items: center;
		font-size: 0.85rem;
		font-weight: 600;
		color: #f15a24;
		margin-bottom: 4px;
	}

	.chat-thread__attachments-title {
		font-weight: 600;
	}

	.chat-thread__attachments-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.attachment-preview {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: white;
		border: 1px solid rgba(241, 90, 36, 0.2);
		border-radius: 6px;
		font-size: 0.85rem;
		pointer-events: auto;
		transition: all 0.2s ease;
	}

	.attachment-preview:hover {
		background: rgba(241, 90, 36, 0.05);
		border-color: rgba(241, 90, 36, 0.4);
	}

	.attachment-preview__icon {
		color: #f15a24;
		flex-shrink: 0;
	}

	.attachment-preview__name {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: #1f1f1f;
	}

	.attachment-preview__remove {
		flex-shrink: 0;
		color: #8f8f8f;
	}

	.message-bubble__attachments {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 8px;
	}

	.message-attachment {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: rgba(255, 255, 255, 0.3);
		border-radius: 8px;
		transition: background-color 0.2s;
		cursor: pointer;
	}

	.message-bubble--outgoing .message-attachment {
		background: rgba(255, 255, 255, 0.2);
	}

	.message-attachment:hover {
		background: rgba(255, 255, 255, 0.4);
	}

	.message-bubble--outgoing .message-attachment:hover {
		background: rgba(255, 255, 255, 0.3);
	}

	.message-attachment__icon {
		flex-shrink: 0;
		color: inherit;
	}

	.message-attachment__link {
		flex: 1;
		color: inherit;
		font-size: 0.9rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.message-attachment__download {
		flex-shrink: 0;
		color: inherit;
		opacity: 0.7;
	}

	.message-attachment:hover .message-attachment__download {
		opacity: 1;
	}

	/* Mobile optimizations */
	@media (max-width: 960px) {
		.floating-chat {
			right: 16px;
			bottom: 16px;
			left: auto;
			top: auto;
			align-items: flex-end;
			gap: 16px;
		}

		.floating-chat--open {
			right: 0 !important;
			bottom: 0 !important;
			left: 0 !important;
			top: 0 !important;
			width: 100vw !important;
			height: 100vh !important;
			max-width: 100vw !important;
			max-height: 100vh !important;
			align-items: stretch;
			gap: 0;
			position: fixed !important;
		}

		.floating-chat--open .floating-button {
			display: none;
		}

		.floating-button {
			position: relative;
		}

		.floating-chat--open .chat-container {
			width: 100% !important;
			height: 100% !important;
			max-width: 100% !important;
			max-height: 100% !important;
			border-radius: 0 !important;
			display: flex;
			flex-direction: column;
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
		}

		.chat-header {
			padding: 12px 16px;
			flex-shrink: 0;
			position: relative;
		}

		.chat-header__headline {
			font-size: 1rem;
		}

		.chat-header__label {
			font-size: 0.7rem;
		}

		.chat-header__title {
			margin-left: 40px;
		}

		.chat-mobile-conversations-toggle {
			position: absolute;
			left: 12px;
			top: 50%;
			transform: translateY(-50%);
			z-index: 10;
			flex-shrink: 0;
		}

		.chat-header__title {
			margin-left: 40px;
			flex: 1;
			min-width: 0;
		}

		.chat-body {
			flex: 1;
			min-height: 0;
			padding: 12px;
			max-height: none;
			position: relative;
		}

		.chat-mobile-overlay {
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.5);
			z-index: 1;
			animation: fadeIn 0.2s ease-out;
		}

		.chat-conversations {
			position: fixed;
			top: 0;
			left: 0;
			bottom: 0;
			width: 85%;
			max-width: 320px;
			z-index: 2;
			transform: translateX(-100%);
			transition: transform 0.3s ease-out;
			box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
			border-radius: 0;
		}

		.chat-conversations--mobile-open {
			transform: translateX(0);
		}

		.chat-conversations__header {
			padding-top: 50px;
			position: relative;
			gap: 8px;
		}

		.chat-conversations__title {
			flex: 1;
			min-width: 0;
		}

		.chat-conversations__header-actions {
			gap: 4px;
		}

		.chat-conversations__close-mobile {
			position: relative;
			flex-shrink: 0;
		}

		.chat-conversations__new-text {
			display: none;
		}

		.chat-conversations__new {
			padding: 0 4px;
		}

		.chat-conversations__new .v-icon {
			margin: 0 !important;
		}

		.chat-thread {
			width: 100%;
			flex: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;
			padding: 8px 12px 12px;
			position: relative;
			z-index: 0;
			overflow: hidden;
		}

		.chat-thread__header {
			margin-bottom: 8px;
			padding-bottom: 8px;
			border-bottom: 1px solid rgba(241, 90, 36, 0.1);
		}

		.chat-thread__header h4 {
			font-size: 0.95rem;
		}

		.chat-thread__header small {
			font-size: 0.8rem;
		}

		.chat-thread__messages {
			flex: 1;
			min-height: 0;
			padding-right: 4px;
			gap: 10px;
		}

		.message-bubble {
			max-width: 85%;
			padding: 10px 14px;
			font-size: 0.9rem;
		}

		.message-bubble__text {
			font-size: 0.9rem;
			line-height: 1.5;
		}

		.message-bubble__time {
			font-size: 0.7rem;
			margin-top: 4px;
		}

		.chat-thread__footer {
			margin-top: 12px;
			padding-top: 12px;
			border-top: 1px solid rgba(241, 90, 36, 0.1);
			flex-shrink: 0;
			gap: 8px;
			flex-direction: column;
			width: 100%;
		}

		.chat-thread__input-wrapper {
			width: 100%;
		}

		.chat-thread__attachments-preview {
			width: 100%;
			padding: 10px;
		}

		.chat-thread__attachments-header {
			font-size: 0.8rem;
		}

		.attachment-preview {
			padding: 6px 10px;
			font-size: 0.8rem;
		}

		.chat-thread__attach--active {
			background-color: rgba(241, 90, 36, 0.15) !important;
		}

		.chat-thread__input {
			flex: 1;
		}

		.chat-thread__input ::v-deep .v-input__control {
			min-height: 44px;
		}

		.chat-thread__input ::v-deep .v-text-field__details {
			display: none;
		}

		.chat-thread__send {
			flex-shrink: 0;
		}

		/* Safe area para dispositivos com notch */
		@supports (padding: max(0px)) {
			.floating-chat {
				padding-bottom: max(0px, env(safe-area-inset-bottom));
			}

			.chat-thread__footer {
				padding-bottom: max(12px, env(safe-area-inset-bottom));
			}
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	/* Ajustes para telas muito pequenas */
	@media (max-width: 360px) {
		.chat-conversations {
			width: 90%;
		}

		.message-bubble {
			max-width: 90%;
			padding: 8px 12px;
		}
	}

	/* Melhorias gerais para mobile */
	@media (max-width: 960px) and (orientation: portrait) {
		.chat-container {
			height: 100vh;
			height: -webkit-fill-available;
		}
	}

	.annotation-editor-dialog__body ::v-deep .ql-container {
		min-height: 220px;
		font-size: 0.95rem;
	}

	.vademecum-editor-dialog__body {
		max-height: 70vh;
	}

	.vademecum-editor-dialog__search {
		border: 1px solid #eee;
		border-radius: 8px;
		max-height: 180px;
		overflow: auto;
	}

	.vademecum-editor-dialog__check {
		align-items: flex-start;
	}

	.vademecum-editor-dialog__lei {
		display: inline-block;
		white-space: normal;
		line-height: 1.35;
		padding-top: 2px;
	}
</style>

<style>
/* Tooltip do Mévio: estilos globais (conteúdo é renderizado em portal) */
.floating-chat-meio-tooltip {
	max-width: 280px;
	padding: 12px 16px !important;
	background: linear-gradient(135deg, #363636 0%, #2d2d2d 100%) !important;
	color: #fff !important;
	border-radius: 12px !important;
	box-shadow:
		0 10px 40px rgba(0, 0, 0, 0.25),
		0 0 0 1px rgba(241, 90, 36, 0.25),
		0 4px 0 0 #F15A24 !important;
	font-family: inherit !important;
	line-height: 1.4 !important;
	border: none !important;
}

.floating-chat-meio-tooltip .meio-tooltip__content {
	display: flex;
	align-items: center;
	gap: 10px;
}

.floating-chat-meio-tooltip .meio-tooltip__icon {
	color: #F15A24 !important;
	flex-shrink: 0;
}

.floating-chat-meio-tooltip .meio-tooltip__text {
	font-size: 0.9375rem;
	font-weight: 500;
	letter-spacing: 0.01em;
	color: #fff !important;
}

/* Animação de entrada/saída suave */
.meio-tooltip-transition-enter-active,
.meio-tooltip-transition-leave-active {
	transition: opacity 0.25s ease, transform 0.25s ease;
}

.meio-tooltip-transition-enter,
.meio-tooltip-transition-leave-to {
	opacity: 0;
	transform: translateY(8px);
}
</style>
